import { ROUTES, isSageIDUser, isAuth0Authenticated, getSageIdAdditionalInfoUrl } from '@futrli/shared-services';
import React, { useState, useEffect } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';

import { INVITE_USER_ROUTE, MFA_SELECTION_ROUTE, SAGE_50_TRIGGER, SFA_TRIGGER } from '../../core/constants';
import { SageInvite } from '../../invite/components/sage-invite';
import { getInvitationLandingPath } from '../../utils/invite';
import { useGetRedirectInfoFromQueryParam } from '../components/use-get-redirect-info-from-query-param';
import { useDispatch } from 'react-redux';
import { getCurrentAuthenticatedUser } from '../redux-utils/actions';
import { Sage50Trigger } from '../../../../auth/src/sage-50/sage-50-trigger';
import { SfaTrigger } from '../../../../auth/src/sfa/sfa-trigger';

interface Props {
  component: React.ComponentType;
}

/**
 * Manages the routing when an user is authenticated.
 * @returns {JSX.Element}
 */
export const AuthenticatedRoutes: React.FC<Props> = ({ component: Component }: Props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isHandlingRedirect, setIsHandlingRedirect] = useState(true);
  const { isLoadingRedirectInfo, redirectInfo, onRedirect } = useGetRedirectInfoFromQueryParam();
  const isLoading = isHandlingRedirect || isLoadingRedirectInfo;

  /*
   * Checks if the user has to land in a specific route, at the moment just checking
   * if the user is coming from the invitation flow, if it is the case, redirect user to additional info page
   * where user will submit provision request and by doing that accept invitation.
   *
   */
  const checkForSpecificLandingPage = async () => {
    const invitationLandingPage = getInvitationLandingPath();

    if (!invitationLandingPage) {
      return setIsHandlingRedirect(false);
    }

    // If there is saved invitation landing page, sageId users should firstly be
    // redirected to additional info page where they need to enter business type and where
    // workspace provision request is submitted
    const user = await dispatch(getCurrentAuthenticatedUser());
    if (isSageIDUser(user) || isAuth0Authenticated()) {
      setIsHandlingRedirect(false);
      navigate(getSageIdAdditionalInfoUrl());
      return;
    }
  };

  useEffect(() => {
    if (isLoadingRedirectInfo) {
      return;
    }

    if (redirectInfo) {
      onRedirect();
      return;
    }

    checkForSpecificLandingPage();
  }, [isLoadingRedirectInfo, redirectInfo]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) {
    return null;
  }

  return (
    <Routes>
      <Route path={MFA_SELECTION_ROUTE} element={<Navigate to={ROUTES.ROOT_ROUTE} replace />} />
      <Route path={SFA_TRIGGER} element={<SfaTrigger />} />
      <Route path={SAGE_50_TRIGGER} element={<Sage50Trigger />} />
      <Route path={INVITE_USER_ROUTE} element={<SageInvite />} />

      {/* This should only render momentarily after sign in while we wait for a redirect to happen */}
      <Route path={ROUTES.SAGE_ID_SIGN_IN_TRIGGER} element={null} />

      <Route path="*" element={<Component />} />
    </Routes>
  );
};
